import { useEffect, useState } from 'react';
import type { ShareLink, TripWithItems } from '@shared/types';
import { api, ApiError } from '../lib/api';

export function ShareSheet({ trip, onClose }: { trip: TripWithItems; onClose: () => void }) {
  const [links, setLinks] = useState<ShareLink[]>([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [managing, setManaging] = useState(false);
  const [emails, setEmails] = useState('');
  const [inviteNote, setInviteNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .listShareLinks(trip.id)
      .then((r) => setLinks(r.links))
      .catch(() => setError('Could not load share links.'));
  }, [trip.id]);

  const active = links.filter((l) => !l.revoked);
  // One trip, one link: the oldest active link is THE link — the one already
  // circulating. Extra links exist for separate audiences and live behind
  // "Manage links".
  const primary = active.length ? active[active.length - 1] : null;
  const others = active.filter((l) => l !== primary);

  async function createLink() {
    setBusy(true);
    setError(null);
    try {
      const link = await api.createShareLink(trip.id);
      setLinks((prev) => [link, ...prev]);
      await copy(link.url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create a link.');
    } finally {
      setBusy(false);
    }
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(url);
      setTimeout(() => setCopied(null), 2500);
    } catch {
      // Clipboard is blocked in some webviews; the input below stays selectable.
      setError('Copy the link from the box below.');
    }
  }

  async function revoke(token: string) {
    await api.revokeShareLink(trip.id, token);
    setLinks((prev) => prev.map((l) => (l.token === token ? { ...l, revoked: true } : l)));
  }

  async function sendInvites(e: React.FormEvent) {
    e.preventDefault();
    const list = emails
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!list.length) return;

    setBusy(true);
    setError(null);
    try {
      const result = await api.invite(trip.id, list);
      setEmails('');
      setInviteNote(
        result.note ??
          `Added ${result.invited} ${result.invited === 1 ? 'person' : 'people'} to this trip.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add those people.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Share this trip"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-t-2xl sm:rounded-2xl
                   bg-surface p-6 pb-8"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-display text-[1.5rem] leading-tight text-ink">Share this trip</h2>
        <p className="mt-2 font-body text-[0.9375rem] text-muted">
          Anyone with the link can read the whole trip. They only need an account to save it.
        </p>

        {!primary ? (
          <button onClick={createLink} disabled={busy} className="btn-primary mt-5 w-full">
            {busy ? 'Creating…' : 'Create your share link'}
          </button>
        ) : (
          <div className="mt-5">
            <input
              readOnly
              value={primary.url}
              onFocus={(e) => e.currentTarget.select()}
              className="field !text-meta"
              aria-label="Your share link"
            />
            <div className="mt-1.5 flex items-center gap-1">
              <button onClick={() => copy(primary.url)} className="btn-primary !py-1.5 !text-meta">
                {copied === primary.url ? 'Copied' : 'Copy link'}
              </button>
              <span className="ml-auto font-util text-meta text-muted">
                {primary.useCount} {primary.useCount === 1 ? 'open' : 'opens'}
              </span>
            </div>

            {(others.length > 0 || managing) && (
              <div className="mt-5 border-t border-ink/10 pt-4">
                <p className="font-util text-meta font-semibold text-ink">Other links</p>
                <p className="mt-1 font-util text-meta text-muted">
                  A separate link can be revoked for one group without breaking
                  the link everyone else already has.
                </p>
                <div className="mt-3 space-y-3">
                  {others.map((link) => (
                    <div key={link.token}>
                      <input
                        readOnly
                        value={link.url}
                        onFocus={(e) => e.currentTarget.select()}
                        className="field !text-meta"
                        aria-label="Share link"
                      />
                      <div className="mt-1.5 flex items-center gap-1">
                        <button onClick={() => copy(link.url)} className="btn-quiet !py-1 !text-meta">
                          {copied === link.url ? 'Copied' : 'Copy link'}
                        </button>
                        <button onClick={() => revoke(link.token)} className="btn-quiet !py-1 !text-meta">
                          Revoke
                        </button>
                        <span className="ml-auto font-util text-meta text-muted">
                          {link.useCount} {link.useCount === 1 ? 'open' : 'opens'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={createLink} disabled={busy} className="btn-quiet mt-2 !text-meta">
                  {busy ? 'Creating…' : 'Create a separate link'}
                </button>
              </div>
            )}

            {others.length === 0 && !managing && (
              <button
                onClick={() => setManaging(true)}
                className="btn-quiet mt-3 !text-meta text-muted"
              >
                Need a separate link for a different group?
              </button>
            )}
          </div>
        )}

        <form onSubmit={sendInvites} className="mt-7 border-t border-ink/10 pt-5">
          <label className="font-util text-meta font-semibold text-ink" htmlFor="invite-emails">
            Add people by email
          </label>
          <input
            id="invite-emails"
            className="field mt-2"
            placeholder="friend@example.com, another@example.com"
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
          />
          <button type="submit" disabled={busy} className="btn-quiet mt-2 !text-meta">
            Add to this trip
          </button>
          {inviteNote && <p className="mt-2 font-util text-meta text-muted">{inviteNote}</p>}
        </form>

        {error && <p className="mt-3 font-util text-meta text-[#B4462F]">{error}</p>}

        <button onClick={onClose} className="btn-quiet mt-5 w-full">
          Done
        </button>
      </div>
    </div>
  );
}
